import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { parseTheme, THEME_STORAGE_KEY, type ThemeId } from '@/lib/themes';

interface ThemeContextValue {
  readonly theme: ThemeId;
  readonly setTheme: (theme: ThemeId) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

function readStoredTheme(): ThemeId {
  try {
    return parseTheme(window.localStorage.getItem(THEME_STORAGE_KEY));
  } catch {
    // Private-mode browsers throw on localStorage; the default theme is the safe answer.
    return parseTheme(null);
  }
}

/**
 * The active theme, as `data-theme` on <html>. Switching only swaps which block of CSS
 * variables applies, so nothing remounts: a video keeps playing through a change.
 * Every theme is dark, so the `dark` class stays on for the components that branch on it.
 */
export function ThemeProvider(props: { readonly children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemeId>(readStoredTheme);

  useEffect(() => {
    const root = document.documentElement;
    root.dataset['theme'] = theme;
    root.classList.add('dark');
  }, [theme]);

  const setTheme = useCallback((next: ThemeId): void => {
    setThemeState(next);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // Not persisting is a smaller failure than refusing to change the theme.
    }
  }, []);

  const value = useMemo<ThemeContextValue>(() => ({ theme, setTheme }), [theme, setTheme]);
  return <ThemeContext.Provider value={value}>{props.children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const value = useContext(ThemeContext);
  if (value === undefined) {
    throw new Error('useTheme must be used inside a ThemeProvider.');
  }
  return value;
}
